// Excel-Export eines DevExtreme-DataGrids mit getrennter Bildschirm- und Exportansicht.
// Der Bildschirm zeigt nur wenige Spalten (z. B. Verkäufe: sieben), die Excel-Datei soll aber unverändert alle
// bisherigen Spalten in Definitionsreihenfolge enthalten. Dafür schaltet exportWithColumns kurz in die
// Exportansicht (alle Spalten außer `exclude` sichtbar, visibleIndex = Definitionsindex), ruft den bestehenden
// Exporter (exportToExcel(false)) und stellt nach dem Export die Bildschirmansicht wieder her.
// Dateiname, customizeExcelCell, Filter, Suche, Sortierung, Gruppierung und Summen kommen wie bisher aus dem Grid.
//
// Rückgabe: true, wenn ein Export läuft (Bildschirmansicht kommt danach zurück), sonst false. Optional `onRestore`:
// wird nach jeder Wiederherstellung der Bildschirmansicht aufgerufen.
// - Lädt das Grid gerade, lehnt der Exporter ab (loadAll) und es käme kein Abschluss: dann wird nicht umgeschaltet.
// - Abschluss = Ereignis „fileSaving“ mit demselben Ereignisobjekt wie „exporting“ dieses Exports (DevExtreme reicht
//   beiden dasselbe Objekt; „exported“ trägt keins). So stellt ein früherer, noch laufender Export die Ansicht nicht
//   zurück, während ein neuerer seine Zeilen noch in der Exportansicht aufbereitet.
// - Abbruch über onExporting der Seite (e.cancel): Bildschirmansicht sofort zurück, Rückgabe false.
// - Bricht ein gestarteter Export später ab (kein „fileSaving“), stellt der nächste Export die Ansicht zuerst zurück.

// je Grid die Wiederherstellung des zuletzt gestarteten, noch nicht abgeschlossenen Exports
const pending = new WeakMap();

// visibleIndex mehrerer Spalten setzen. DevExtreme 20.2 nummeriert nach jedem einzelnen Setzen alle Spalten neu, die
// gerade gesetzte gewinnt bei gleichem Index (columns_controller.js updateColumnVisibleIndexes, normalizeIndexes). In
// aufsteigender Folge der Zielwerte bleiben die schon gesetzten Spalten deshalb vorn stehen; in Definitionsfolge
// verschoben spätere Setzungen die früheren (ab dem zweiten Export standen die Bildschirmspalten vertauscht).
function setVisibleIndexes(grid, targets) {
  targets
    .map((target, i) => ({ i, target }))
    .filter((entry) => typeof entry.target === 'number')
    .sort((a, b) => a.target - b.target || a.i - b.i)
    .forEach((entry) => grid.columnOption(entry.i, 'visibleIndex', entry.target));
}

export function exportWithColumns(grid, { exclude = [], onRestore = null } = {}) {
  if (!grid) {
    return false;
  }
  const dataSource = grid.getDataSource();
  if (dataSource && dataSource.isLoading()) {
    return false;
  }
  // Ansicht eines nicht abgeschlossenen Exports zuerst zurückstellen (meldet auch dessen Handler ab)
  const stale = pending.get(grid);
  if (stale) {
    stale();
  }
  const count = grid.columnCount();

  // 1. Bildschirmansicht merken
  const saved = [];
  for (let i = 0; i < count; i++) {
    saved.push({
      visible: grid.columnOption(i, 'visible'),
      visibleIndex: grid.columnOption(i, 'visibleIndex')
    });
  }

  // 2. einmalige Wiederherstellung nach genau diesem Export
  let exportArgs = null;
  let done = false;
  const onExporting = (e) => {
    if (!exportArgs) {
      exportArgs = e;
    }
  };
  const onFileSaving = (e) => {
    if (exportArgs && e === exportArgs) {
      restore();
    }
  };
  const restore = () => {
    if (done) {
      return;
    }
    done = true;
    if (pending.get(grid) === restore) {
      pending.delete(grid);
    }
    grid.off('exporting', onExporting);
    grid.off('fileSaving', onFileSaving);
    grid.beginUpdate();
    try {
      // erst die Sichtbarkeit aller Spalten, dann die Reihenfolge aufsteigend (setVisibleIndexes)
      saved.forEach((s, i) => {
        grid.columnOption(i, 'visible', s.visible);
      });
      setVisibleIndexes(grid, saved.map((s) => s.visibleIndex));
    } finally {
      grid.endUpdate();
    }
    if (typeof onRestore === 'function') {
      onRestore();
    }
  };
  pending.set(grid, restore);
  grid.on('exporting', onExporting);
  grid.on('fileSaving', onFileSaving);

  try {
    // 3. Exportansicht: alle Spalten außer `exclude` sichtbar, Reihenfolge = Definitionsreihenfolge
    grid.beginUpdate();
    try {
      const order = [];
      for (let i = 0; i < count; i++) {
        const key = grid.columnOption(i, 'name') || grid.columnOption(i, 'dataField');
        grid.columnOption(i, 'visible', exclude.indexOf(key) === -1);
        order.push(i);
      }
      setVisibleIndexes(grid, order);
    } finally {
      grid.endUpdate();
    }

    // 4. bestehender Exporter; „exporting“ (mit dem onExporting der Seite) läuft synchron darin
    grid.exportToExcel(false);
  } catch (e) {
    // sofortiger Fehler: kein Abschluss zu erwarten, Bildschirmansicht gleich zurück
    restore();
    throw e;
  }
  grid.off('exporting', onExporting);
  if (!exportArgs || exportArgs.cancel) {
    restore();
    return false;
  }
  return true;
}
